import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../config";
import ResimUpload from "../ResimUpload";
import { 
  Plus, Edit, Trash2, Search, Eye, Heart, Image as ImageIcon, 
  Video, Music, MapPin, BookOpen, X, Send, 
  Youtube, Globe, Sparkles, Calendar, Type, CheckCircle, XCircle
} from "lucide-react";

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
];

const t = {
  tr: {
    title: "Mezarlıklar / Dijital Anıtlar",
    addMemorial: "Yeni Anıt Ekle",
    editMemorial: "Anıt Düzenle",
    deleteMemorial: "Anıt Sil",
    confirmDelete: "Bu anıtı silmek istediğinize emin misiniz?",
    fullname: "Ad Soyad",
    birthDate: "Doğum Tarihi",
    birthDateText: "Doğum Tarihi (Metin)",
    birthDatePicker: "Doğum Tarihi (Takvim)",
    deathDate: "Vefat Tarihi",
    deathDateText: "Vefat Tarihi (Metin)",
    deathDatePicker: "Vefat Tarihi (Takvim)",
    birthPlaceholder: "Örn: 15 Mayıs 1950, İstanbul",
    deathPlaceholder: "Örn: 20 Ocak 2024, Ankara",
    city: "Şehir",
    cemeteryName: "Mezarlık Adı",
    graveNo: "Mezar No",
    familyNote: "Aile Notu",
    photo: "Profil Fotoğrafı",
    uploadPhoto: "Fotoğraf Yükle",
    location: "Konum",
    locationAddress: "Google Maps Adresi / Konum",
    locationHelp: "Google Maps'ten paylaş butonu ile kopyaladığınız linki yapıştırın",
    status: "Durum",
    active: "Aktif",
    inactive: "Pasif",
    save: "Kaydet",
    cancel: "İptal",
    search: "Ara...",
    all: "Tümü",
    gallery: "Galeri",
    condolences: "Anı Defteri",
    inOurHearts: "Kalbimizdesin",
    viewCount: "görüntülenme",
    condolenceCount: "mesaj",
    mediaCount: "medya",
    noData: "Henüz veri yok",
    loading: "Yükleniyor...",
    lifeStories: "Hayat Hikayesi",
    addStory: "+ Yeni Bölüm Ekle",
    storyTitle: "Bölüm Başlığı",
    storyContent: "İçerik",
    storyPlaceholder: "Çocukluğu, gençliği, askerliği, evliliği, iş hayatı, başarıları...",
    religious: "Dini İçerikler",
    addReligiousItem: "+ Yeni Dini İçerik Ekle",
    religiousType: "Tür",
    religiousTypeQuran: "📖 Kuran-ı Kerim",
    religiousTypeHadith: "📜 Hadis-i Şerif",
    religiousTypeNote: "🙏 Dua / Not",
    religiousLink: "Link (YouTube için)",
    religiousText: "Metin / İçerik",
    inOurHeartsTitle: "Kalbimizdesin",
    addMemory: "+ Yeni Anı Ekle",
    memoryTitle: "Başlık",
    memoryDescription: "Açıklama",
    memoryImage: "Görsel",
    condolencePlaceholder: "Taziye mesajınızı yazın...",
    sendCondolence: "Mesaj Gönder",
    yourName: "Adınız",
    pendingApproval: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    approve: "Onayla",
    reject: "Reddet",
    deleteCondolence: "Mesajı Sil",
    basicInfo: "Temel Bilgiler",
    mapPreview: "Harita Görünümü",
    viewOnMap: "Haritada Aç",
    send: "Gönder",
    close: "Kapat",
    details: "Detaylar",
    writeHere: "Buraya yazın...",
    saveSuccess: "✅ Başarıyla kaydedildi",
    saveError: "❌ Kayıt sırasında hata oluştu",
    deleteSuccess: "✅ Anıt silindi",
    deleteError: "❌ Silme sırasında hata oluştu",
    approveSuccess: "✅ Mesaj onaylandı",
    rejectSuccess: "❌ Mesaj reddedildi",
    deleteCondolenceSuccess: "✅ Mesaj silindi",
  },
  en: {
    title: "Cemeteries / Digital Memorials",
    addMemorial: "Add Memorial",
    editMemorial: "Edit Memorial",
    deleteMemorial: "Delete Memorial",
    confirmDelete: "Are you sure you want to delete this memorial?",
    fullname: "Full Name",
    birthDate: "Birth Date",
    birthDateText: "Birth Date (Text)",
    birthDatePicker: "Birth Date (Calendar)",
    deathDate: "Death Date",
    deathDateText: "Death Date (Text)",
    deathDatePicker: "Death Date (Calendar)",
    birthPlaceholder: "e.g. May 15, 1950, Istanbul",
    deathPlaceholder: "e.g. Jan 20, 2024, Ankara",
    city: "City",
    cemeteryName: "Cemetery Name",
    graveNo: "Grave No",
    familyNote: "Family Note",
    photo: "Profile Photo",
    uploadPhoto: "Upload Photo",
    location: "Location",
    locationAddress: "Google Maps Address / Location",
    locationHelp: "Paste the link you copied from Google Maps share button",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    save: "Save",
    cancel: "Cancel",
    search: "Search...",
    all: "All",
    gallery: "Gallery",
    condolences: "Guest Book",
    inOurHearts: "In Our Hearts",
    viewCount: "views",
    condolenceCount: "messages",
    mediaCount: "media",
    noData: "No data yet",
    loading: "Loading...",
    lifeStories: "Life Story",
    addStory: "+ Add New Chapter",
    storyTitle: "Chapter Title",
    storyContent: "Content",
    storyPlaceholder: "Childhood, youth, military service, marriage, career, achievements...",
    religious: "Religious Content",
    addReligiousItem: "+ Add Religious Content",
    religiousType: "Type",
    religiousTypeQuran: "📖 Quran",
    religiousTypeHadith: "📜 Hadith",
    religiousTypeNote: "🙏 Prayer / Note",
    religiousLink: "Link (for YouTube)",
    religiousText: "Text / Content",
    inOurHeartsTitle: "In Our Hearts",
    addMemory: "+ Add Memory",
    memoryTitle: "Title",
    memoryDescription: "Description",
    memoryImage: "Image",
    condolencePlaceholder: "Write your condolence message...",
    sendCondolence: "Send Message",
    yourName: "Your Name",
    pendingApproval: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    approve: "Approve",
    reject: "Reject",
    deleteCondolence: "Delete Message",
    basicInfo: "Basic Information",
    mapPreview: "Map Preview",
    viewOnMap: "View on Map",
    send: "Send",
    close: "Close",
    details: "Details",
    writeHere: "Write here...",
    saveSuccess: "✅ Saved successfully",
    saveError: "❌ Error while saving",
    deleteSuccess: "✅ Memorial deleted",
    deleteError: "❌ Error while deleting",
    approveSuccess: "✅ Message approved",
    rejectSuccess: "❌ Message rejected",
    deleteCondolenceSuccess: "✅ Message deleted",
  },
  de: {
    title: "Friedhöfe / Digitale Denkmäler",
    addMemorial: "Denkmal hinzufügen",
    editMemorial: "Denkmal bearbeiten",
    deleteMemorial: "Denkmal löschen",
    confirmDelete: "Möchten Sie dieses Denkmal wirklich löschen?",
    fullname: "Vollständiger Name",
    birthDate: "Geburtsdatum",
    birthDateText: "Geburtsdatum (Text)",
    birthDatePicker: "Geburtsdatum (Kalender)",
    deathDate: "Sterbedatum",
    deathDateText: "Sterbedatum (Text)",
    deathDatePicker: "Sterbedatum (Kalender)",
    birthPlaceholder: "z.B. 15. Mai 1950, Istanbul",
    deathPlaceholder: "z.B. 20. Januar 2024, Ankara",
    city: "Stadt",
    cemeteryName: "Friedhofsname",
    graveNo: "Grabnummer",
    familyNote: "Familiennotiz",
    photo: "Profilfoto",
    uploadPhoto: "Foto hochladen",
    location: "Standort",
    locationAddress: "Google Maps Adresse / Standort",
    locationHelp: "Fügen Sie den Link ein, den Sie über die Teilen-Schaltfläche von Google Maps kopiert haben",
    status: "Status",
    active: "Aktiv",
    inactive: "Inaktiv",
    save: "Speichern",
    cancel: "Abbrechen",
    search: "Suchen...",
    all: "Alle",
    gallery: "Galerie",
    condolences: "Gästebuch",
    inOurHearts: "In unseren Herzen",
    viewCount: "Aufrufe",
    condolenceCount: "Nachrichten",
    mediaCount: "Medien",
    noData: "Keine Daten",
    loading: "Laden...",
    lifeStories: "Lebensgeschichte",
    addStory: "+ Neuen Abschnitt hinzufügen",
    storyTitle: "Abschnittstitel",
    storyContent: "Inhalt",
    storyPlaceholder: "Kindheit, Jugend, Militärdienst, Heirat, Karriere, Erfolge...",
    religious: "Religiöse Inhalte",
    addReligiousItem: "+ Religiösen Inhalt hinzufügen",
    religiousType: "Typ",
    religiousTypeQuran: "📖 Koran",
    religiousTypeHadith: "📜 Hadith",
    religiousTypeNote: "🙏 Gebet / Notiz",
    religiousLink: "Link (für YouTube)",
    religiousText: "Text / Inhalt",
    inOurHeartsTitle: "In unseren Herzen",
    addMemory: "+ Erinnerung hinzufügen",
    memoryTitle: "Titel",
    memoryDescription: "Beschreibung",
    memoryImage: "Bild",
    condolencePlaceholder: "Schreiben Sie Ihre Beileidsnachricht...",
    sendCondolence: "Nachricht senden",
    yourName: "Ihr Name",
    pendingApproval: "Ausstehend",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    approve: "Genehmigen",
    reject: "Ablehnen",
    deleteCondolence: "Nachricht löschen",
    basicInfo: "Grundinformationen",
    mapPreview: "Kartenvorschau",
    viewOnMap: "Auf Karte anzeigen",
    send: "Senden",
    close: "Schließen",
    details: "Details",
    writeHere: "Hier schreiben...",
    saveSuccess: "✅ Erfolgreich gespeichert",
    saveError: "❌ Fehler beim Speichern",
    deleteSuccess: "✅ Denkmal gelöscht",
    deleteError: "❌ Fehler beim Löschen",
    approveSuccess: "✅ Nachricht genehmigt",
    rejectSuccess: "❌ Nachricht abgelehnt",
    deleteCondolenceSuccess: "✅ Nachricht gelöscht",
  },
};

const getLang = () => {
  const fromApp = localStorage.getItem("app_lang") || "tr";
  return ["tr", "en", "de"].includes(fromApp) ? fromApp : "tr";
};

// YouTube URL'den embed link çıkarma
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = (match && match[2].length === 11) ? match[2] : null;
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url.includes("youtube.com/embed/") ? url : null;
};

// Google Maps URL'den embed link çıkarma
const getGoogleMapsEmbedUrl = (url) => {
  if (!url) return null;
  if (url.includes("maps.google.com") || url.includes("google.com/maps")) {
    if (url.includes("/embed")) return url;
    const match = url.match(/q=([^&]+)/);
    if (match) {
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyDummyKey&q=${encodeURIComponent(match[1])}`;
    }
  }
  return url;
};

export default function MezarliklarPage() {
  const [lang, setLang] = useState(getLang);
  const TT = t[lang];
  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [newStory, setNewStory] = useState({ title: "", content: "" });
  const [newReligious, setNewReligious] = useState({ type: "quran", link: "", text: "" });
  const [newMemory, setNewMemory] = useState({ title: "", description: "", image_url: "" });
  const [newCondolence, setNewCondolence] = useState({ sender_name: "", message: "" });
  const [submittingCondolence, setSubmittingCondolence] = useState(false);

  const [formData, setFormData] = useState({
    fullname: "", 
    birth_date_text: "", birth_date_picker: "",
    death_date_text: "", death_date_picker: "",
    city: "", cemetery_name: "", grave_no: "",
    family_note: "", life_stories: [], 
    religious_items: [],
    memories: [],
    photo_url: "", location_address: "", status: "active"
  });

  useEffect(() => {
    const handleLangChange = () => setLang(getLang());
    window.addEventListener("app_lang_change", handleLangChange);
    return () => window.removeEventListener("app_lang_change", handleLangChange);
  }, []);

  const fetchMemorials = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setLoading(true);
    try {
      let url = `${API_BASE}/cemetery/memorials?limit=100`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) setMemorials(data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/cemetery/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) setStats(data.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchMemorials(); fetchStats(); }, [fetchMemorials, fetchStats]);

  const handleSave = async () => {
    if (!formData.fullname.trim()) { alert(TT.fullname + " " + (lang === "tr" ? "zorunludur" : "is required")); return; }
    setSaving(true);
    const token = localStorage.getItem("token");
    
    const birth_date = formData.birth_date_picker || formData.birth_date_text;
    const death_date = formData.death_date_picker || formData.death_date_text;
    
    const payload = {
      ...formData,
      birth_date,
      death_date,
    };
    
    try {
      let url = `${API_BASE}/cemetery/memorials`;
      let method = "POST";
      if (selectedMemorial) { url = `${API_BASE}/cemetery/memorials/${selectedMemorial.id}`; method = "PUT"; }
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.ok) {
        setShowModal(false); setSelectedMemorial(null); resetForm();
        fetchMemorials(); fetchStats();
        alert(TT.saveSuccess);
      } else alert(data.message || TT.saveError);
    } catch (err) { console.error(err); alert(TT.saveError); }
    finally { setSaving(false); }
  };

  const resetForm = () => setFormData({
    fullname: "", birth_date_text: "", birth_date_picker: "",
    death_date_text: "", death_date_picker: "",
    city: "", cemetery_name: "", grave_no: "",
    family_note: "", life_stories: [], religious_items: [], memories: [],
    photo_url: "", location_address: "", status: "active"
  });

  const handleEdit = (memorial) => {
    setSelectedMemorial(memorial);
    setFormData({
      fullname: memorial.fullname || "",
      birth_date_text: memorial.birth_date_text || "",
      birth_date_picker: memorial.birth_date_picker || "",
      death_date_text: memorial.death_date_text || "",
      death_date_picker: memorial.death_date_picker || "",
      city: memorial.city || "",
      cemetery_name: memorial.cemetery_name || "",
      grave_no: memorial.grave_no || "",
      family_note: memorial.family_note || "",
      life_stories: memorial.life_stories || [],
      religious_items: memorial.religious_items || [],
      memories: memorial.memories || [],
      photo_url: memorial.photo_url || "",
      location_address: memorial.location_address || "",
      status: memorial.status || "active"
    });
    setShowModal(true);
  };

  const handleDelete = async (memorial) => {
    if (!window.confirm(`${memorial.fullname} - ${TT.confirmDelete}`)) return;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/cemetery/memorials/${memorial.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) { fetchMemorials(); fetchStats(); alert(TT.deleteSuccess); }
      else alert(data.message || TT.deleteError);
    } catch (err) { console.error(err); alert(TT.deleteError); }
  };

  // --- Anı Defteri Yönetimi Fonksiyonları ---
  const handleApproveCondolence = async (condolenceId, memorialId) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/cemetery/condolences/${condolenceId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: "approved" })
      });
      const data = await res.json();
      if (data.ok) {
        alert(TT.approveSuccess);
        // Detay modalını güncelle
        const refreshRes = await fetch(`${API_BASE}/cemetery/memorials/${memorialId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const refreshData = await refreshRes.json();
        if (refreshData.ok) setShowDetailModal(refreshData.data);
        fetchMemorials();
      }
    } catch (err) { console.error(err); alert("Hata oluştu"); }
  };

  const handleRejectCondolence = async (condolenceId, memorialId) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/cemetery/condolences/${condolenceId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: "rejected" })
      });
      const data = await res.json();
      if (data.ok) {
        alert(TT.rejectSuccess);
        const refreshRes = await fetch(`${API_BASE}/cemetery/memorials/${memorialId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const refreshData = await refreshRes.json();
        if (refreshData.ok) setShowDetailModal(refreshData.data);
        fetchMemorials();
      }
    } catch (err) { console.error(err); alert("Hata oluştu"); }
  };

  const handleDeleteCondolence = async (condolenceId, memorialId) => {
    if (!window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) return;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API_BASE}/cemetery/condolences/${condolenceId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) {
        alert(TT.deleteCondolenceSuccess);
        const refreshRes = await fetch(`${API_BASE}/cemetery/memorials/${memorialId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const refreshData = await refreshRes.json();
        if (refreshData.ok) setShowDetailModal(refreshData.data);
        fetchMemorials();
      }
    } catch (err) { console.error(err); alert("Hata oluştu"); }
  };

  const addLifeStory = () => {
    if (!newStory.title.trim()) { alert(TT.storyTitle + " " + (lang === "tr" ? "giriniz" : "required")); return; }
    setFormData({ ...formData, life_stories: [...formData.life_stories, { title: newStory.title, content: newStory.content }] });
    setNewStory({ title: "", content: "" });
  };

  const removeLifeStory = (index) => {
    const updated = [...formData.life_stories];
    updated.splice(index, 1);
    setFormData({ ...formData, life_stories: updated });
  };

  const updateLifeStory = (index, field, value) => {
    const updated = [...formData.life_stories];
    updated[index][field] = value;
    setFormData({ ...formData, life_stories: updated });
  };

  const addReligiousItem = () => {
    if (!newReligious.text && !newReligious.link) { alert("İçerik giriniz"); return; }
    setFormData({ ...formData, religious_items: [...formData.religious_items, { ...newReligious }] });
    setNewReligious({ type: "quran", link: "", text: "" });
  };

  const removeReligiousItem = (index) => {
    const updated = [...formData.religious_items];
    updated.splice(index, 1);
    setFormData({ ...formData, religious_items: updated });
  };

  const addMemory = () => {
    if (!newMemory.title.trim()) { alert("Başlık giriniz"); return; }
    setFormData({ ...formData, memories: [...formData.memories, { ...newMemory }] });
    setNewMemory({ title: "", description: "", image_url: "" });
  };

  const removeMemory = (index) => {
    const updated = [...formData.memories];
    updated.splice(index, 1);
    setFormData({ ...formData, memories: updated });
  };

  const sendCondolence = async () => {
    if (!newCondolence.sender_name.trim() || !newCondolence.message.trim()) {
      alert(TT.yourName + " " + (lang === "tr" ? "ve mesaj zorunludur" : "and message are required"));
      return;
    }
    setSubmittingCondolence(true);
    try {
      const res = await fetch(`${API_BASE}/public/cemetery/condolences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          memorial_id: showDetailModal?.id,
          sender_name: newCondolence.sender_name,
          message: newCondolence.message
        })
      });
      const data = await res.json();
      if (data.ok) {
        alert(data.message);
        setNewCondolence({ sender_name: "", message: "" });
        fetchMemorials();
        if (showDetailModal) {
          const refreshRes = await fetch(`${API_BASE}/cemetery/memorials/${showDetailModal.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          });
          const refreshData = await refreshRes.json();
          if (refreshData.ok) setShowDetailModal(refreshData.data);
        }
      } else alert(data.message || "Mesaj gönderilemedi");
    } catch (err) { console.error(err); alert("Bir hata oluştu"); }
    finally { setSubmittingCondolence(false); }
  };

  const getStatusColor = (status) => status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800";
  const getStatusText = (status) => status === "active" ? TT.active : TT.inactive;
  const mapEmbed = getGoogleMapsEmbedUrl(formData.location_address);

  const getReligiousIcon = (type) => {
    switch(type) {
      case "quran": return "📖";
      case "hadith": return "📜";
      default: return "🙏";
    }
  };

  const getReligiousTypeText = (type) => {
    switch(type) {
      case "quran": return TT.religiousTypeQuran;
      case "hadith": return TT.religiousTypeHadith;
      default: return TT.religiousTypeNote;
    }
  };

  const getCondolenceStatusColor = (status) => {
    switch(status) {
      case "approved": return "bg-green-100 text-green-700";
      case "rejected": return "bg-red-100 text-red-700";
      default: return "bg-yellow-100 text-yellow-700";
    }
  };

  const getCondolenceStatusText = (status) => {
    switch(status) {
      case "approved": return TT.approved;
      case "rejected": return TT.rejected;
      default: return TT.pendingApproval;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{TT.title}</h1>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500"><div className="text-2xl font-bold">{stats.toplam_anit || 0}</div><div className="text-sm text-gray-500">Toplam Anıt</div></div>
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500"><div className="text-2xl font-bold text-green-600">{stats.aktif_anit || 0}</div><div className="text-sm text-gray-500">Aktif</div></div>
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500"><div className="text-2xl font-bold text-blue-600">{stats.toplam_goruntulenme || 0}</div><div className="text-sm text-gray-500">{TT.viewCount}</div></div>
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-rose-500"><div className="text-2xl font-bold text-rose-600">{stats.toplam_taziye || 0}</div><div className="text-sm text-gray-500">{TT.condolenceCount}</div></div>
              <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500"><div className="text-2xl font-bold text-purple-600">{stats.toplam_medya || 0}</div><div className="text-sm text-gray-500">{TT.mediaCount}</div></div>
            </div>
          )}
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 justify-between">
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setStatusFilter("all")} className={`px-4 py-2 rounded-lg font-medium transition ${statusFilter === "all" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{TT.all}</button>
              <button onClick={() => setStatusFilter("active")} className={`px-4 py-2 rounded-lg font-medium transition ${statusFilter === "active" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{TT.active}</button>
              <button onClick={() => setStatusFilter("inactive")} className={`px-4 py-2 rounded-lg font-medium transition ${statusFilter === "inactive" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{TT.inactive}</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><input type="text" placeholder={TT.search} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-orange-500" /></div>
              <button onClick={() => { setSelectedMemorial(null); resetForm(); setShowModal(true); }} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"><Plus size={18} />{TT.addMemorial}</button>
            </div>
          </div>
        </div>

        {/* Memorial List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-12 text-gray-500">{TT.loading}</div>
          ) : memorials.length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">{TT.noData}</div>
          ) : (
            memorials.map((memorial) => (
              <div key={memorial.id} className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition">
                <div className="flex p-4 gap-4">
                  <div className="w-16 h-16 flex-shrink-0">
                    {memorial.photo_url ? (
                      <img src={memorial.photo_url} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-2xl">👤</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-gray-900 truncate">{memorial.fullname}</h3>
                    <div className="text-sm text-gray-500">
                      {memorial.birth_date_text || memorial.birth_date_picker?.slice(0,4) || "?"} - {memorial.death_date_text || memorial.death_date_picker?.slice(0,4) || "?"}
                    </div>
                    <div className="text-sm text-gray-500 truncate">{memorial.cemetery_name || "-"}</div>
                    <div className="mt-1"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(memorial.status)}`}>{getStatusText(memorial.status)}</span></div>
                  </div>
                </div>
                <div className="border-t px-4 py-3 flex justify-between items-center bg-gray-50">
                  <div className="flex gap-3 text-xs text-gray-500">
                    <span title={TT.viewCount}>👁️ {memorial.view_count || 0}</span>
                    <span title={TT.condolenceCount}>💬 {memorial.condolences_count || 0}</span>
                    <span title={TT.mediaCount}>📷 {memorial.memories?.length || 0}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(memorial)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title={TT.editMemorial}><Edit size={16} /></button>
                    <button onClick={() => setShowDetailModal(memorial)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title={TT.details}><Eye size={16} /></button>
                    <button onClick={() => handleDelete(memorial)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title={TT.deleteMemorial}><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">{selectedMemorial ? TT.editMemorial : TT.addMemorial}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2 border-b mb-6">
                <button onClick={() => setActiveTab("basic")} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === "basic" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700"}`}>📋 {TT.basicInfo}</button>
                <button onClick={() => setActiveTab("stories")} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === "stories" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700"}`}>📖 {TT.lifeStories}</button>
                <button onClick={() => setActiveTab("religious")} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === "religious" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700"}`}>🕌 {TT.religious}</button>
                <button onClick={() => setActiveTab("memories")} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === "memories" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700"}`}>💖 {TT.inOurHearts}</button>
                <button onClick={() => setActiveTab("location")} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === "location" ? "bg-orange-600 text-white" : "bg-gray-100 text-gray-700"}`}>📍 {TT.location}</button>
              </div>

              {/* BASIC TAB */}
              {activeTab === "basic" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2 flex justify-center mb-4">
                    <div className="relative">
                      {formData.photo_url ? (
                        <img src={formData.photo_url} className="w-24 h-24 rounded-full object-cover border-4 border-orange-200" />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-4xl">👤</div>
                      )}
                      <div className="absolute -bottom-2 -right-2">
                        <ResimUpload onUpload={(url) => setFormData({ ...formData, photo_url: url })} />
                      </div>
                    </div>
                  </div>
                  <input type="text" placeholder={TT.fullname} value={formData.fullname} onChange={(e) => setFormData({ ...formData, fullname: e.target.value })} className="p-3 border rounded-lg col-span-2" />
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{TT.birthDate}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="relative">
                        <Type size={16} className="absolute left-3 top-3 text-gray-400" />
                        <input type="text" placeholder={TT.birthPlaceholder} value={formData.birth_date_text} onChange={(e) => setFormData({ ...formData, birth_date_text: e.target.value })} className="w-full p-2 pl-9 border rounded-lg" />
                        <span className="text-xs text-gray-400 ml-1">{TT.birthDateText}</span>
                      </div>
                      <div className="relative">
                        <Calendar size={16} className="absolute left-3 top-3 text-gray-400" />
                        <input type="date" value={formData.birth_date_picker} onChange={(e) => setFormData({ ...formData, birth_date_picker: e.target.value })} className="w-full p-2 pl-9 border rounded-lg" />
                        <span className="text-xs text-gray-400 ml-1">{TT.birthDatePicker}</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{TT.deathDate}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="relative">
                        <Type size={16} className="absolute left-3 top-3 text-gray-400" />
                        <input type="text" placeholder={TT.deathPlaceholder} value={formData.death_date_text} onChange={(e) => setFormData({ ...formData, death_date_text: e.target.value })} className="w-full p-2 pl-9 border rounded-lg" />
                        <span className="text-xs text-gray-400 ml-1">{TT.deathDateText}</span>
                      </div>
                      <div className="relative">
                        <Calendar size={16} className="absolute left-3 top-3 text-gray-400" />
                        <input type="date" value={formData.death_date_picker} onChange={(e) => setFormData({ ...formData, death_date_picker: e.target.value })} className="w-full p-2 pl-9 border rounded-lg" />
                        <span className="text-xs text-gray-400 ml-1">{TT.deathDatePicker}</span>
                      </div>
                    </div>
                  </div>

                  <input type="text" placeholder={TT.city} value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="p-3 border rounded-lg" />
                  <input type="text" placeholder={TT.cemeteryName} value={formData.cemetery_name} onChange={(e) => setFormData({ ...formData, cemetery_name: e.target.value })} className="p-3 border rounded-lg" />
                  <input type="text" placeholder={TT.graveNo} value={formData.grave_no} onChange={(e) => setFormData({ ...formData, grave_no: e.target.value })} className="p-3 border rounded-lg" />
                  <textarea placeholder={TT.familyNote} value={formData.family_note} onChange={(e) => setFormData({ ...formData, family_note: e.target.value })} className="p-3 border rounded-lg col-span-2" rows="2" />
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="p-3 border rounded-lg col-span-2">
                    <option value="active">{TT.active}</option>
                    <option value="inactive">{TT.inactive}</option>
                  </select>
                </div>
              )}

              {/* STORIES TAB */}
              {activeTab === "stories" && (
                <div>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <input type="text" placeholder={TT.storyTitle} value={newStory.title} onChange={(e) => setNewStory({ ...newStory, title: e.target.value })} className="w-full p-2 border rounded-lg mb-2" />
                    <textarea placeholder={TT.storyPlaceholder} value={newStory.content} onChange={(e) => setNewStory({ ...newStory, content: e.target.value })} className="w-full p-2 border rounded-lg mb-2" rows="4" />
                    <button onClick={addLifeStory} className="bg-green-600 text-white px-4 py-2 rounded-lg">{TT.addStory}</button>
                  </div>
                  <div className="space-y-4">
                    {formData.life_stories.map((story, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <input type="text" value={story.title} onChange={(e) => updateLifeStory(idx, "title", e.target.value)} className="text-lg font-bold p-2 border rounded-lg flex-1 mr-2" />
                          <button onClick={() => removeLifeStory(idx)} className="text-red-600 p-2"><Trash2 size={18} /></button>
                        </div>
                        <textarea value={story.content} onChange={(e) => updateLifeStory(idx, "content", e.target.value)} className="w-full p-2 border rounded-lg" rows="6" placeholder={TT.writeHere} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RELIGIOUS TAB */}
              {activeTab === "religious" && (
                <div>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <select value={newReligious.type} onChange={(e) => setNewReligious({ ...newReligious, type: e.target.value })} className="p-2 border rounded-lg">
                        <option value="quran">{TT.religiousTypeQuran}</option>
                        <option value="hadith">{TT.religiousTypeHadith}</option>
                        <option value="note">{TT.religiousTypeNote}</option>
                      </select>
                      <input type="url" placeholder={TT.religiousLink} value={newReligious.link} onChange={(e) => setNewReligious({ ...newReligious, link: e.target.value })} className="p-2 border rounded-lg" />
                      <textarea placeholder={TT.religiousText} value={newReligious.text} onChange={(e) => setNewReligious({ ...newReligious, text: e.target.value })} className="p-2 border rounded-lg" rows="2" />
                    </div>
                    <button onClick={addReligiousItem} className="bg-green-600 text-white px-4 py-2 rounded-lg">{TT.addReligiousItem}</button>
                  </div>
                  <div className="space-y-3">
                    {formData.religious_items.map((item, idx) => (
                      <div key={idx} className="border rounded-lg p-3 flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-bold flex items-center gap-2">
                            <span>{getReligiousIcon(item.type)}</span>
                            <span>{getReligiousTypeText(item.type)}</span>
                          </div>
                          {item.link && (
                            getYouTubeEmbedUrl(item.link) ? (
                              <iframe src={getYouTubeEmbedUrl(item.link)} className="w-full h-48 mt-2 rounded-lg" allowFullScreen></iframe>
                            ) : (
                              <a href={item.link} target="_blank" className="text-blue-600 text-sm break-all">🔗 {item.link}</a>
                            )
                          )}
                          {item.text && <p className="text-gray-700 mt-2 italic">{item.text}</p>}
                        </div>
                        <button onClick={() => removeReligiousItem(idx)} className="text-red-600 p-2"><Trash2 size={18} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MEMORIES TAB */}
              {activeTab === "memories" && (
                <div>
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <input type="text" placeholder={TT.memoryTitle} value={newMemory.title} onChange={(e) => setNewMemory({ ...newMemory, title: e.target.value })} className="w-full p-2 border rounded-lg mb-2" />
                    <textarea placeholder={TT.memoryDescription} value={newMemory.description} onChange={(e) => setNewMemory({ ...newMemory, description: e.target.value })} className="w-full p-2 border rounded-lg mb-2" rows="3" />
                    <div className="flex gap-2 items-center">
                      <input type="url" placeholder={TT.memoryImage} value={newMemory.image_url} onChange={(e) => setNewMemory({ ...newMemory, image_url: e.target.value })} className="flex-1 p-2 border rounded-lg" />
                      <ResimUpload onUpload={(url) => setNewMemory({ ...newMemory, image_url: url })} />
                    </div>
                    <button onClick={addMemory} className="bg-green-600 text-white px-4 py-2 rounded-lg mt-2">{TT.addMemory}</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.memories.map((memory, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        {memory.image_url && <img src={memory.image_url} className="w-full h-48 object-cover" />}
                        <div className="p-3">
                          <div className="flex justify-between items-start">
                            <h4 className="font-bold text-lg">{memory.title}</h4>
                            <button onClick={() => removeMemory(idx)} className="text-red-600"><Trash2 size={16} /></button>
                          </div>
                          <p className="text-gray-600 text-sm mt-1">{memory.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* LOCATION TAB */}
              {activeTab === "location" && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{TT.locationAddress}</label>
                    <input type="text" placeholder="https://maps.google.com/..." value={formData.location_address} onChange={(e) => setFormData({ ...formData, location_address: e.target.value })} className="w-full p-3 border rounded-lg" />
                    <p className="text-xs text-gray-500 mt-1">{TT.locationHelp}</p>
                  </div>
                  {mapEmbed && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{TT.mapPreview}</label>
                      <div className="rounded-lg overflow-hidden border">
                        <iframe src={mapEmbed} className="w-full h-64" title="Konum" allowFullScreen></iframe>
                      </div>
                      <a href={formData.location_address} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm mt-2 inline-flex items-center gap-1"><MapPin size={14} /> {TT.viewOnMap}</a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">{TT.cancel}</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50">{saving ? "⏳" : "💾"} {TT.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal - Anıt Detayı + Anı Defteri Yönetimi */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">{showDetailModal.fullname}</h2>
              <button onClick={() => setShowDetailModal(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6">
              {/* Profil ve temel bilgiler */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <div className="flex-shrink-0 text-center">
                  {showDetailModal.photo_url ? (
                    <img src={showDetailModal.photo_url} className="w-32 h-32 rounded-full object-cover border-4 border-orange-200 mx-auto" />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-5xl mx-auto">👤</div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900">{showDetailModal.fullname}</h3>
                  <div className="flex flex-wrap gap-4 mt-2 text-gray-600">
                    <span>📅 {showDetailModal.birth_date_text || showDetailModal.birth_date_picker?.slice(0,4) || "?"} - {showDetailModal.death_date_text || showDetailModal.death_date_picker?.slice(0,4) || "?"}</span>
                    {showDetailModal.city && <span>📍 {showDetailModal.city}</span>}
                    {showDetailModal.cemetery_name && <span>🪦 {showDetailModal.cemetery_name}</span>}
                  </div>
                  {showDetailModal.grave_no && <p className="text-sm text-gray-500 mt-1">Mezar No: {showDetailModal.grave_no}</p>}
                  {showDetailModal.family_note && <p className="text-gray-600 mt-3 italic">"{showDetailModal.family_note}"</p>}
                </div>
              </div>

              {/* Hayat Hikayesi */}
              {showDetailModal.life_stories?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-orange-700 mb-3 flex items-center gap-2"><BookOpen size={20} /> {TT.lifeStories}</h3>
                  <div className="space-y-4">
                    {showDetailModal.life_stories.map((story, idx) => (
                      <div key={idx} className="border-l-4 border-orange-300 pl-4">
                        <h4 className="font-bold text-gray-800">{story.title}</h4>
                        <p className="text-gray-700 whitespace-pre-wrap mt-1">{story.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dini İçerikler */}
              {showDetailModal.religious_items?.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <h3 className="text-lg font-bold text-orange-700 mb-3 flex items-center gap-2">🕌 {TT.religious}</h3>
                  <div className="space-y-3">
                    {showDetailModal.religious_items.map((item, idx) => (
                      <div key={idx} className="border-b pb-2 last:border-0">
                        <div className="font-bold flex items-center gap-2">
                          <span>{getReligiousIcon(item.type)}</span>
                          <span>{getReligiousTypeText(item.type)}</span>
                        </div>
                        {item.link && (
                          getYouTubeEmbedUrl(item.link) ? (
                            <iframe src={getYouTubeEmbedUrl(item.link)} className="w-full h-48 mt-2 rounded-lg" allowFullScreen></iframe>
                          ) : (
                            <a href={item.link} target="_blank" className="text-blue-600 text-sm break-all">🔗 {item.link}</a>
                          )
                        )}
                        {item.text && <p className="text-gray-700 mt-2 italic">{item.text}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kalbimizdesin */}
              {showDetailModal.memories?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-orange-700 mb-3 flex items-center gap-2"><Heart size={20} /> {TT.inOurHearts}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {showDetailModal.memories.map((memory, idx) => (
                      <div key={idx} className="border rounded-lg overflow-hidden">
                        {memory.image_url && <img src={memory.image_url} className="w-full h-48 object-cover" />}
                        <div className="p-3">
                          <h4 className="font-bold text-gray-800">{memory.title}</h4>
                          <p className="text-gray-600 text-sm mt-1">{memory.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Konum */}
              {showDetailModal.location_address && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-orange-700 mb-3 flex items-center gap-2"><MapPin size={20} /> {TT.location}</h3>
                  {getGoogleMapsEmbedUrl(showDetailModal.location_address) ? (
                    <iframe src={getGoogleMapsEmbedUrl(showDetailModal.location_address)} className="w-full h-64 rounded-lg border" allowFullScreen></iframe>
                  ) : (
                    <a href={showDetailModal.location_address} target="_blank" className="text-blue-600">{showDetailModal.location_address}</a>
                  )}
                </div>
              )}

              {/* Anı Defteri - Yönetimli */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-orange-700 mb-3 flex items-center gap-2"><Heart size={20} /> {TT.condolences}</h3>
                
                {/* Yeni mesaj formu */}
                <div className="bg-gray-50 p-4 rounded-xl mb-4">
                  <input type="text" placeholder={TT.yourName} value={newCondolence.sender_name} onChange={(e) => setNewCondolence({ ...newCondolence, sender_name: e.target.value })} className="w-full p-2 border rounded-lg mb-2" />
                  <textarea placeholder={TT.condolencePlaceholder} value={newCondolence.message} onChange={(e) => setNewCondolence({ ...newCondolence, message: e.target.value })} className="w-full p-2 border rounded-lg mb-2" rows="3" />
                  <button onClick={sendCondolence} disabled={submittingCondolence} className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"><Send size={16} /> {TT.sendCondolence}</button>
                </div>

                {/* Mesaj listesi - yönetim butonları ile */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {showDetailModal.condolences?.length === 0 ? (
                    <div className="text-center text-gray-500 py-4">Henüz mesaj yok. İlk mesajı siz yazın.</div>
                  ) : (
                    showDetailModal.condolences?.map((c, idx) => (
                      <div key={idx} className="border rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-bold text-gray-800">{c.sender_name}</span>
                            <span className="text-xs text-gray-400 ml-2">{new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex gap-1">
                            {c.status === "pending" && (
                              <>
                                <button onClick={() => handleApproveCondolence(c.id, showDetailModal.id)} className="p-1 text-green-600 hover:bg-green-50 rounded" title={TT.approve}><CheckCircle size={16} /></button>
                                <button onClick={() => handleRejectCondolence(c.id, showDetailModal.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title={TT.reject}><XCircle size={16} /></button>
                              </>
                            )}
                            <button onClick={() => handleDeleteCondolence(c.id, showDetailModal.id)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title={TT.deleteCondolence}><Trash2 size={14} /></button>
                          </div>
                        </div>
                        <p className="text-gray-700 mt-1">{c.message}</p>
                        <div className="mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getCondolenceStatusColor(c.status)}`}>
                            {getCondolenceStatusText(c.status)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}